import { useId } from 'react';

import { cn } from '@/lib/utils';

interface LogoMarkProps {
  className?: string;
}

/**
 * Isotipo de EDSuite: la "V"/chevron con degradado del logo corporativo
 * (`assets/img/defaults/logo_empresa.png`).
 */
export function LogoMark({ className }: LogoMarkProps) {
  // `useId` evita que dos instancias del logo compartan el mismo id de gradiente
  // (si se repite, el navegador resuelve a la primera y el icono no se pinta).
  const gradientId = `edsuite-logo-gradient-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;

  return (
    <svg viewBox="0 0 64 64" role="img" aria-label="EDSuite" className={cn('size-9', className)}>
      <defs>
        <linearGradient
          id={gradientId}
          x1="30"
          y1="9"
          x2="19"
          y2="56"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#22d3ee" />
          <stop offset="1" stopColor="#1e3a8a" />
        </linearGradient>
      </defs>
      <path
        d="M29 9 48 32 19 56"
        fill="none"
        stroke={`url(#${gradientId})`}
        strokeWidth="10"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

interface LogoProps {
  className?: string;
  /** `light` = texto blanco (sobre fondos oscuros). */
  tone?: 'light' | 'dark';
  size?: 'sm' | 'md' | 'lg';
  tagline?: string;
}

const SIZES = {
  sm: { mark: 'size-7', text: 'text-base' },
  md: { mark: 'size-9', text: 'text-lg' },
  lg: { mark: 'size-11', text: 'text-2xl' },
} as const;

export function Logo({ className, tone = 'dark', size = 'md', tagline }: LogoProps) {
  const sizing = SIZES[size];

  return (
    <div className={cn('flex items-center gap-2.5', className)}>
      <LogoMark className={sizing.mark} />

      <div className="leading-none">
        <p
          className={cn(
            'font-bold tracking-tight',
            sizing.text,
            tone === 'light' ? 'text-white' : 'text-ink-900',
          )}
        >
          ED<span className={tone === 'light' ? 'text-brand-200' : 'text-brand-600'}>Suite</span>
        </p>
        {tagline ? (
          <p className={cn('mt-1 text-[11px] font-medium', tone === 'light' ? 'text-white/70' : 'text-ink-400')}>
            {tagline}
          </p>
        ) : null}
      </div>
    </div>
  );
}
